import React, { useState } from 'react';
import { HTTP } from 'framework/api';
import OneStopModal from './OneStopModal';
import { ModalData } from './OneStopModalLayout';
import OneStopErrorMessage from './OneStopErrorMessage';
import { DEFAULT_ERROR_MESSAGE } from 'framework/form';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { RequestType } from './oneStopTypes';
import { SanitizedHtml } from 'framework/utils';

const REQUEST_ID_REGEXP = />#[0-9]*</;
const DAVINCI_PROCESS_ID_HEADER = 'davinci-process-id';
const FORBIDDEN_ERROR_MESSAGE = 'You have no Redmine authorization to perform this action.';
const INTERNAL_SERVER_ERROR_MESSAGE =
  'Something went wrong when processing your request and it has not been submitted.';

type RequestContext = {
  type?: RequestType;
};

type OneStopModalDecoratorProps = {
  isOneStop?: boolean;
  children: React.ReactElement;
  submitHandler?: () => void;
  handleMessageModalClose?: () => void;
  messageContext?: string;
  requestContext?: RequestContext;
};

const OneStopModalDecorator = ({
  children,
  isOneStop = true,
  submitHandler,
  handleMessageModalClose,
  messageContext,
  requestContext,
}: OneStopModalDecoratorProps) => {
  const [isError, setIsError] = useState(false);
  const [modalData, setModalData] = useState<Nullable<ModalData>>(null);
  const { addMessage } = useMessageService(messageContext);

  const openModal = (content: string | React.ReactElement, title?: string, requestId?: string | null) => {
    setModalData({ isOpen: true, content, title, requestId, requestType: requestContext?.type });
  };

  const getRequestId = (message: string) => {
    const match = message.match(REQUEST_ID_REGEXP);

    return match && match[0].slice(2, match[0].length - 1);
  };

  const childAfterSubmit = children.props.afterSubmit;

  const handleFormSubmit = (response: unknown) => {
    childAfterSubmit?.execute?.(response);

    const r = response as { message?: string };
    const message = <SanitizedHtml html={r.message ?? ''} />;
    const title = '';
    const requestId = r.message ? getRequestId(r.message) : null;

    setIsError(false);
    openModal(message, title, requestId);

    if (submitHandler) {
      submitHandler();
    }
  };

  const showModalWithSubmissionError = (submitErrors: any, errorMessage: string) => {
    const title = 'Ooops...';
    const currentProcessId = submitErrors.headers.get(DAVINCI_PROCESS_ID_HEADER);
    const message = <OneStopErrorMessage message={errorMessage} processId={currentProcessId} />;

    setIsError(true);
    openModal(message, title);
  };

  const handleErrorsOnSubmit = (submitErrors: any) => {
    switch (submitErrors.status) {
      case HTTP.FORBIDDEN:
        showModalWithSubmissionError(submitErrors, FORBIDDEN_ERROR_MESSAGE);
        break;
      case HTTP.INTERNAL_SERVER_ERROR:
        showModalWithSubmissionError(submitErrors, INTERNAL_SERVER_ERROR_MESSAGE);
        break;
      default:
        addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    }
  };

  return (
    <>
      {React.cloneElement(children, {
        handleErrorsOnSubmit,
        afterSubmit: {
          ...childAfterSubmit,
          execute: handleFormSubmit,
        },
      })}
      <OneStopModal
        data={modalData}
        isError={isError}
        isOneStop={isOneStop}
        handleMessageModalClose={handleMessageModalClose}
      />
    </>
  );
};

export default OneStopModalDecorator;
